import React from 'react';
import PageLayout from '../components/layout/PageLayout';

const PrivacyPolicy: React.FC = () => {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        
        <p className="text-sm text-gray-600 mb-8">
          <strong>Last Updated:</strong> December 20, 2025
        </p>

        <div className="space-y-6 text-gray-700">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Welcome to Ruchira ("we," "our," or "us"). This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our home food delivery platform 
              and mobile application (collectively, the "Service").
            </p>
            <p className="mt-2">
              By using Ruchira, you agree to the collection and use of information in accordance with 
              this policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Personal Information</h3>
            <p>We collect the following personal information when you register and use our Service:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number (for WhatsApp notifications and order updates)</li>
              <li>Delivery address</li>
              <li>Payment information (processed securely through third-party payment providers)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Order Information</h3>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Food items ordered</li>
              <li>Order history</li>
              <li>Delivery preferences</li>
              <li>Special dietary requirements or notes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.3 WhatsApp Communication Data</h3>
            <p>When you opt-in to receive WhatsApp notifications, we collect:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>WhatsApp phone number</li>
              <li>Message delivery status</li>
              <li>Interaction data with automated messages</li>
              <li>Feedback and ratings submitted via WhatsApp</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.4 Technical Information</h3>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Device information (type, operating system)</li>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Usage data and analytics</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
              <li><strong>Order Processing:</strong> To process and fulfill your food orders</li>
              <li><strong>Communication:</strong> To send order confirmations, delivery updates, and customer support messages via WhatsApp, SMS, or email</li>
              <li><strong>Payment Processing:</strong> To process payments and prevent fraud</li>
              <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our Service</li>
              <li><strong>Marketing:</strong> To send promotional offers (only with your consent)</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries and provide assistance</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          {/* WhatsApp Integration */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">4. WhatsApp Business Integration</h2>
            <p>
              Ruchira uses WhatsApp Business API to provide real-time order updates and customer support. 
              By providing your phone number, you consent to receive:
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Order confirmations and status updates</li>
              <li>Delivery notifications</li>
              <li>Customer service messages</li>
              <li>Feedback requests</li>
            </ul>
            <p className="mt-3">
              <strong>Opt-Out:</strong> You can stop receiving WhatsApp messages at any time by replying 
              "STOP" or updating your notification preferences in your account settings.
            </p>
            <p className="mt-2">
              WhatsApp communication is governed by both this Privacy Policy and WhatsApp's own privacy policy. 
              We do not share your WhatsApp data with third parties for marketing purposes.
            </p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. How We Share Your Information</h2>
            <p>We may share your information with:</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.1 Service Providers</h3>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Payment processors (UPI, payment gateway providers)</li>
              <li>Cloud hosting services (Firebase, Google Cloud)</li>
              <li>WhatsApp Business API (Meta Platforms, Inc.)</li>
              <li>Analytics providers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.2 Home Chefs and Delivery Partners</h3>
            <p>
              We share necessary order information (delivery address, contact number, order details) 
              with home chefs and delivery partners to fulfill your orders.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law, court order, or government regulation, 
              or to protect our rights, property, or safety.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Secure payment processing through certified payment gateways</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the internet is 100% secure. While we strive to 
              protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our Service and 
              comply with legal obligations. Order history and transaction records are kept for accounting 
              and tax purposes as required by Indian law.
            </p>
            <p className="mt-2">
              You may request deletion of your account and personal data by contacting us at 
              privacy@ruchira.app. Some information may be retained for legal or legitimate business purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, contact us at privacy@ruchira.app or through your account settings.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, 
              and improve our Service. You can control cookie settings through your browser preferences.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Third-Party Links</h2>
            <p>
              Our Service may contain links to third-party websites or services. We are not responsible 
              for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Children's Privacy</h2>
            <p>
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect 
              personal information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              by posting the new policy on this page and updating the "Last Updated" date. Your continued use 
              of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our data practices, contact us:</p>
            <div className="mt-3 bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> privacy@ruchira.app</p>
              <p><strong>Support:</strong> support@ruchira.app</p>
              <p><strong>Address:</strong> Xarvin, Bengaluru, Karnataka, India</p>
              <p><strong>Website:</strong> https://ruchira.app</p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">14. Governing Law</h2>
            <p>
              This Privacy Policy is governed by the laws of India. Any disputes shall be subject to the 
              exclusive jurisdiction of courts in Bengaluru, Karnataka.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-600">
          <p>© 2025 Ruchira by Xarvin. All rights reserved.</p>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;
